import {
    AwsEC2InstanceType,
    DedicatedHostOSCoreResults,
    DedicatedHostRecommendation,
    DedicatedHostServer,
    EC2Server, PaymentSummary, PaymentType, Pricing,
    SharedTenancyOSCostResults, VMWareCluster, VMWareCostCluster
} from '@amzn/awscat-portfolio-assessment-types';
import { Alert, Select, SelectProps, SpaceBetween, TabsProps } from '@amzn/awsui-components-react';

import { defaultSortBy } from '../../../common/defaultTableSortState';
import { constants } from '../../../common/Localization';
import { formatCurrencyNoDecimals, formatNumberAsString, formatPreInstalledSw } from '../../../common/LocalizationHelper';
import { getUserGuidePage } from '../../../common/resourceConstants';
import AWSCATTable from '../../../common/table/Table-index';
import { ColumnDefinition, ColumnTypes, Query } from '../../../common/table/types';
import { compare, roundToInteger } from '../../../common/util';
import { getCell } from '../../common/awsui-table-helper';
import { CashFlowData } from '../common/types';

export const buildEC2CostDetailsTabs = (
    ec2InstanceTypesContent: JSX.Element,
    ec2CashFlowContent: JSX.Element,
    ec2PaymentOptionDetailContent: JSX.Element,
    riDiscountContent: JSX.Element,
    ec2PaymentSummaryContent: JSX.Element,
    paymentTypeContent: JSX.Element,
    dedicatedHostDetailContent: JSX.Element | null,
    dedicatedHostOSCostContent: JSX.Element | null,
    vmWareClusterCostContent: JSX.Element | null,
    vmWareClusterCashFlowContent: JSX.Element | null,
): TabsProps.Tab[] => {
    const tabs: TabsProps.Tab[] = [
        {
            id: 'costPerInstance',
            label: constants.EC2_INSTANCE_TYPES,
            content: ec2InstanceTypesContent,
        },
        {
            id: 'cashFlow',
            label: constants.EC2_CASH_FLOWS,
            content: ec2CashFlowContent,
        },
        {
            id: 'paymentOptions',
            label: constants.EC2_PRICING_DETAIL,
            content: ec2PaymentOptionDetailContent,
        },
        {
            id: 'reservedInstanceDiscount',
            label: constants.EC2_RI_VOLUME_DISCOUNT,
            content: riDiscountContent,
        },
        {
            id: 'priceSummary',
            label: constants.EC2_PAYMENT_OPTION_SUMMARY,
            content: ec2PaymentSummaryContent,
        },
        {
            id: 'paymentType',
            label: constants.EC2_PAYMENT_OPTION_DETAIL,
            content: paymentTypeContent,
        },
    ];

    if (dedicatedHostDetailContent) {
        tabs.push({
            id: 'dedicatedHostDetails',
            label: constants.DEDICATED_HOST_DETAILS,
            content: dedicatedHostDetailContent,
        });
    }

    if (dedicatedHostOSCostContent) {
        tabs.push({
            id: 'dedicatedHostOSCost',
            label: constants.DEDICATED_HOST_OS_COST,
            content: dedicatedHostOSCostContent,
        });
    }

    if (vmWareClusterCostContent) {
        tabs.push({
            id: 'vmwareClusterCost',
            label: constants.VMWARE_CLOUD_CLUSTERS,
            content: vmWareClusterCostContent,
        });
    }
    if (vmWareClusterCashFlowContent) {
        tabs.push({
            id: 'vmwareClusterCashFlow',
            label: constants.VMWARE_CLOUD_CASH_FLOWS,
            content: vmWareClusterCashFlowContent,
        });
    }

    return tabs;
};

export const ec2CostPerInstanceColumns = (currency: string, pricing?: Pricing): ColumnDefinition<AwsEC2InstanceType>[] => [
    {
        id: 'instanceType',
        header: constants.INSTANCE_TYPE,
        cell: item => item.instanceType,
        type: ColumnTypes.TEXT,
        text: constants.INSTANCE_TYPE,
    },
    {
        id: 'operatingSystem',
        header: constants.OPERATING_SYSTEM,
        data: item => item.operatingSystem ?? '-',
        cell: item => item.operatingSystem ?? '-',
        type: ColumnTypes.TEXT,
        text: constants.OPERATING_SYSTEM,
    },
    {
        id: 'preInstalledSw',
        header: constants.PREINSTALLED_SOFTWARE,
        data: item => formatPreInstalledSw(item.preInstalledSw),
        cell: item => formatPreInstalledSw(item.preInstalledSw),
        type: ColumnTypes.TEXT,
        text: constants.PREINSTALLED_SOFTWARE,
    },
    {
        id: 'tenancy',
        header: constants.TENANCY,
        cell: item => item.tenancy,
        type: ColumnTypes.TEXT,
        text: constants.TENANCY,
    },
    {
        id: pricing ? 'recommendedPayment' : 'lowestOfferName',
        header: constants.RECOMMENDED_PAYMENT_OPTION,
        data: item => pricing ? pricing.offerDetails[item.lowestOfferName] : item.lowestOfferName,
        cell: item => pricing ? pricing.offerDetails[item.lowestOfferName] : item.lowestOfferName,
        type: ColumnTypes.TEXT,
        text: constants.RECOMMENDED_PAYMENT_OPTION,
        sortingComparator: (itemA: AwsEC2InstanceType, itemB: AwsEC2InstanceType) => {
            const offerA = pricing ? pricing.offerDetails[itemA.lowestOfferName] : itemA.lowestOfferName;
            const offerB = pricing ? pricing.offerDetails[itemB.lowestOfferName] : itemB.lowestOfferName;
            return compare(offerA, offerB);
        }
    },
    {
        id: 'numInstances',
        header: constants.COUNT,
        cell: item => getCell(formatNumberAsString(item.numInstances)),
        type: ColumnTypes.NUMBER,
        text: constants.COUNT,
    },
    {
        id: 'totalCost',
        header: constants.TCO_TOTAL_COST,
        data: item => roundToInteger(item.totalCost),
        cell: item => getCell(formatCurrencyNoDecimals(item.totalCost, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.TCO_TOTAL_COST,
    },
];

export const EC2InstanceTypesTable = (items: AwsEC2InstanceType[], currency: string, pricing?: Pricing): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.EC2_INSTANCE_TYPES}
            items={items}
            columnDefinitions={ec2CostPerInstanceColumns(currency, pricing)}
            defaultSortBy={defaultSortBy('instanceType', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};

const ec2CashFlowColumns = (currency: string, duration: number, pricing?: Pricing): ColumnDefinition<EC2Server | DedicatedHostServer>[] => {
    const columns: ColumnDefinition<EC2Server | DedicatedHostServer>[] = [
        {
            id: 'serverId',
            header: constants.SERVER_ID,
            cell: item => item.serverId,
            type: ColumnTypes.TEXT,
            text: constants.SERVER_ID,
        },
        {
            id: 'instanceType',
            header: constants.INSTANCE_TYPE,
            cell: item => item.instanceType,
            type: ColumnTypes.TEXT,
            text: constants.INSTANCE_TYPE,
        },
        {
            id: 'tenancy',
            header: constants.TENANCY,
            cell: item => item.tenancy,
            type: ColumnTypes.TEXT,
            text: constants.TENANCY,
        },
        {
            id: pricing ? 'recommendedPayment' : 'lowestOfferName',
            header: constants.RECOMMENDED_PAYMENT_OPTION,
            data: item => pricing ? pricing.offerDetails[item.lowestOfferName] : item.lowestOfferName,
            cell: item => pricing ? pricing.offerDetails[item.lowestOfferName] : item.lowestOfferName,
            type: ColumnTypes.TEXT,
            text: constants.RECOMMENDED_PAYMENT_OPTION,
            sortingComparator: (itemA: EC2Server | DedicatedHostServer, itemB: EC2Server | DedicatedHostServer) => {
                const offerA = pricing ? pricing.offerDetails[itemA.lowestOfferName] : itemA.lowestOfferName;
                const offerB = pricing ? pricing.offerDetails[itemB.lowestOfferName] : itemB.lowestOfferName;
                return compare(offerA, offerB);
            }
        },
    ];

    if (duration > 0) {
        new Array(duration).fill(0).forEach((_, year) => {
            columns.push({
                id: `year${year}`,
                header: `${constants.YEAR} ${year + 1}`,
                data: item => roundToInteger(item.offers[item.lowestOfferName]?.cashflow.yearly[year]),
                cell: item => getCell(formatCurrencyNoDecimals(item.offers[item.lowestOfferName]?.cashflow.yearly[year], currency)),
                type: ColumnTypes.NUMBER,
                sortingComparator: (itemA: EC2Server | DedicatedHostServer, itemB: EC2Server | DedicatedHostServer) => {
                    const costA = itemA.offers[itemA.lowestOfferName]?.cashflow.yearly[year];
                    const costB = itemB.offers[itemB.lowestOfferName]?.cashflow.yearly[year];
                    return compare(costA, costB);
                }
            });
        });
    }

    columns.push({
        id: 'residual',
        header: constants.RESIDUAL,
        data: item => roundToInteger(item.offers[item.lowestOfferName]?.cashflow.residual),
        cell: item => getCell(formatCurrencyNoDecimals(item.offers[item.lowestOfferName]?.cashflow.residual, currency)),
        type: ColumnTypes.NUMBER,
        sortingComparator: (itemA: EC2Server | DedicatedHostServer, itemB: EC2Server | DedicatedHostServer) => {
            const residualA = itemA.offers[itemA.lowestOfferName]?.cashflow.residual;
            const residualB = itemB.offers[itemB.lowestOfferName]?.cashflow.residual;
            return compare(residualA, residualB);
        }
    });

    return columns;
};

export const EC2CashFlowTable = (items: (EC2Server | DedicatedHostServer)[], currency: string, duration: number, pricing?: Pricing): JSX.Element => {  
    return (
        <AWSCATTable
            resourceName={constants.EC2_CASH_FLOWS}
            items={items}
            columnDefinitions={ec2CashFlowColumns(currency, duration, pricing)}
            defaultSortBy={defaultSortBy('serverId', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};

export const compareOfferTerm = (pricing?: Pricing) => (offer1: string, offer2: string) => {
    return pricing ? -pricing.offerDetails[offer1].localeCompare(pricing.offerDetails[offer2]) : offer1.localeCompare(offer2);
};

export const ec2PaymentDetailColumns = (currency: string, pricing?: Pricing): ColumnDefinition<EC2Server | DedicatedHostServer>[] => {
    const columns: ColumnDefinition<EC2Server | DedicatedHostServer>[] = [
        {
            id: 'serverId',
            header: constants.SERVER_ID,
            cell: item => item.serverId,
            type: ColumnTypes.TEXT,
            text: constants.SERVER_ID,
        },
        {
            id: 'instanceType',
            header: constants.INSTANCE_TYPE,
            cell: item => item.instanceType,
            type: ColumnTypes.TEXT,
            text: constants.INSTANCE_TYPE,
        },
        {
            id: 'operatingSystem',
            header: constants.OPERATING_SYSTEM,
            cell: item => item.operatingSystem,
            type: ColumnTypes.TEXT,
            text: constants.OPERATING_SYSTEM,
        },
        {
            id: 'preInstalledSw',
            header: constants.PREINSTALLED_SOFTWARE,
            data: item => formatPreInstalledSw(item.preInstalledSw),
            cell: item => formatPreInstalledSw(item.preInstalledSw),
            type: ColumnTypes.TEXT,
            text: constants.PREINSTALLED_SOFTWARE,
        },
        {
            id: 'tenancy',
            header: constants.TENANCY,
            cell: item => item.tenancy,
            type: ColumnTypes.TEXT,
            text: constants.TENANCY,
        },
        {
            id: 'recommendedPayment',
            header: constants.RECOMMENDED_PAYMENT_OPTION,
            data: item => pricing ? pricing.offerDetails[item.lowestOfferName] : item.lowestOfferName,
            cell: item => pricing ? pricing.offerDetails[item.lowestOfferName] : item.lowestOfferName,
            type: ColumnTypes.TEXT,
            text: constants.RECOMMENDED_PAYMENT_OPTION,
            sortingComparator: (itemA: EC2Server | DedicatedHostServer, itemB: EC2Server | DedicatedHostServer) => {
                const offerA = pricing ? pricing.offerDetails[itemA.lowestOfferName] : itemA.lowestOfferName;
                const offerB = pricing ? pricing.offerDetails[itemB.lowestOfferName] : itemB.lowestOfferName;
                return compare(offerA, offerB);
            }
        },
    ];

    const offerCodes: string[] = Object.keys(pricing?.offerDetails ?? {}).sort(compareOfferTerm(pricing));
    offerCodes.forEach(offerCode => {
        columns.push({
            id: offerCode,
            header: pricing?.offerDetails[offerCode] ?? offerCode,
            data: item => roundToInteger(item.offers[offerCode]?.total),
            cell: item => getCell(formatCurrencyNoDecimals(item.offers[offerCode]?.total, currency)),
            type: ColumnTypes.NUMBER,
            sortingComparator: (itemA: EC2Server | DedicatedHostServer, itemB: EC2Server | DedicatedHostServer) => {
                const costA = itemA.offers[offerCode]?.total;
                const costB = itemB.offers[offerCode]?.total;
                return compare(costA, costB);
            }
        });
    });

    return columns;
};

export const EC2PaymentDetailTable = (items: (EC2Server | DedicatedHostServer)[], currency: string, pricing?: Pricing, defaultQuery?: Query): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.EC2_PRICING_DETAIL}
            items={items}
            columnDefinitions={ec2PaymentDetailColumns(currency, pricing)}
            defaultSortBy={defaultSortBy('serverId', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
            defaultQuery={defaultQuery}
        />
    );
};

export const buildRIDiscountCashFlow = (yearlyCashFlow: number[]): CashFlowData[] => {
    const riDiscountCashFlow = {
        type: constants.RI_VOLUME_DISCOUNT,
    };
    
    new Array(yearlyCashFlow.length).fill(0).forEach((_, yearIndex) => {
        (riDiscountCashFlow as CashFlowData)['year' + yearIndex] = yearlyCashFlow[yearIndex];
    });

    return [ riDiscountCashFlow as CashFlowData ];
};

const ec2PaymentSummaryColumns = (currency: string, pricing?: Pricing): ColumnDefinition<PaymentSummary>[] => [
    {
        id: 'paymentType',
        header: constants.PAYMENT_TYPE,
        data: item => pricing?.offerDetails[item.offerTerm] ?? item.offerTerm,
        cell: item => pricing?.offerDetails[item.offerTerm] ?? item.offerTerm,
        type: ColumnTypes.TEXT,
        text: constants.PAYMENT_TYPE,
        sortingComparator: (itemA: PaymentSummary, itemB: PaymentSummary) => {
            const offerA = pricing?.offerDetails[itemA.offerTerm] ?? itemA.offerTerm;
            const offerB = pricing?.offerDetails[itemB.offerTerm] ?? itemB.offerTerm;
            return compare(offerA, offerB);
        }
    },
    {
        id: 'totalUpfront',
        header: constants.TOTAL_UPFRONT_COST,
        data: item => roundToInteger(item.totalUpfront),
        cell: item => getCell(formatCurrencyNoDecimals(item.totalUpfront, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.TOTAL_UPFRONT_COST,
    },
    {
        id: 'totalMonthly',
        header: constants.TOTAL_MONTHLY_COST,
        data: item => roundToInteger(item.totalMonthly),
        cell: item => getCell(formatCurrencyNoDecimals(item.totalMonthly, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.TOTAL_MONTHLY_COST,
    },
    {
        id: 'riDiscount',
        header: constants.RI_TIER_DISCOUNT,
        data: item => roundToInteger(-item.discountSpecs.value),
        cell: item => getCell(formatCurrencyNoDecimals(-item.discountSpecs.value, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.RI_TIER_DISCOUNT,
        sortingComparator: (itemA: PaymentSummary, itemB: PaymentSummary) => {
            const discountA = itemA.discountSpecs.value;
            const discountB = itemB.discountSpecs.value;
            return compare(discountA, discountB);
        }
    },
    {
        id: 'totalResidual',
        header: constants.TOTAL_RESIDUAL,
        data: item => roundToInteger(item.totalResidual),
        cell: item => getCell(formatCurrencyNoDecimals(item.totalResidual, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.TOTAL_RESIDUAL,
    },
    {
        id: 'totalCost',
        header: constants.TCO_TOTAL_COST,
        data: item => roundToInteger(item.totalCost - item.discountSpecs.value),
        cell: item => 
            getCell(formatCurrencyNoDecimals(item.totalCost - item.discountSpecs.value, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.TCO_TOTAL_COST,
    },
];

export const EC2PaymentSummaryTable = (items: PaymentSummary[], currency: string, pricing?: Pricing): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.EC2_PAYMENT_OPTION_SUMMARY}
            items={items}
            columnDefinitions={ec2PaymentSummaryColumns(currency, pricing)}
            defaultSortBy={defaultSortBy('paymentType', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};

export const paymentTypeTabContent = (
    selectedOption: SelectProps.Option | null,
    options: SelectProps.Options,
    setSelectedOption: (option: SelectProps.Option) => void,
    tableItems: PaymentType[],
    currency: string,
): JSX.Element => {
    return (
        <SpaceBetween size='l'>
            <Select
                options={options}
                selectedOption={selectedOption}
                onChange={({ detail }) => setSelectedOption(detail.selectedOption)}
                placeholder={constants.SELECT_A_PAYMENT_TYPE}
            />
            {EC2PaymentTypeTable(tableItems, currency, selectedOption)}
        </SpaceBetween>
    );
};

const ec2PaymentTypeColumns = (currency: string, selectedOffer: string): ColumnDefinition<PaymentType>[] => [
    {
        id: 'instanceType',
        header: constants.EC2_TYPE,
        cell: item => item.instanceType,
        type: ColumnTypes.TEXT,
        text: constants.EC2_TYPE,
    },
    {
        id: 'operatingSystem',
        header: constants.OPERATING_SYSTEM,
        cell: item => item.operatingSystem,
        type: ColumnTypes.TEXT,
        text: constants.OPERATING_SYSTEM,
    },
    {
        id: 'preInstalledSw',
        header: constants.PREINSTALLED_SOFTWARE,
        data: item => formatPreInstalledSw(item.preInstalledSw),
        cell: item => formatPreInstalledSw(item.preInstalledSw),
        type: ColumnTypes.TEXT,
        text: constants.PREINSTALLED_SOFTWARE,
    },
    {
        id: 'numInstances',
        header: constants.COUNT,
        cell: item => item.numInstances,
        type: ColumnTypes.TEXT,
        text: constants.COUNT,
    },
    {
        id: 'upfrontCost',
        header: constants.TOTAL_UPFRONT_COST,
        data: item => roundToInteger(item.offers[selectedOffer].upfrontCost),
        cell: item => getCell(formatCurrencyNoDecimals(item.offers[selectedOffer].upfrontCost, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.TOTAL_UPFRONT_COST,
        sortingComparator: (itemA: PaymentType, itemB: PaymentType) => {
            const costA = itemA.offers[selectedOffer].upfrontCost;
            const costB = itemB.offers[selectedOffer].upfrontCost;
            return compare(costA, costB);
        }
    },
    {
        id: 'monthlyCost',
        header: constants.AGGREGATED_MONTHLY_COST,
        data: item => roundToInteger(item.offers[selectedOffer].monthlyCost),
        cell: item => getCell(formatCurrencyNoDecimals(item.offers[selectedOffer].monthlyCost, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.AGGREGATED_MONTHLY_COST,
        sortingComparator: (itemA: PaymentType, itemB: PaymentType) => {
            const discountA = itemA.offers[selectedOffer].monthlyCost;
            const discountB = itemB.offers[selectedOffer].monthlyCost;
            return compare(discountA, discountB);
        }
    },
    {
        id: 'totalCost',
        header: constants.TCO_TOTAL_COST,
        data: item => roundToInteger(item.offers[selectedOffer].totalCost),
        cell: item => getCell(formatCurrencyNoDecimals(item.offers[selectedOffer].totalCost, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.TCO_TOTAL_COST,
        sortingComparator: (itemA: PaymentType, itemB: PaymentType) => {
            const totalA = itemA.offers[selectedOffer].totalCost;
            const totalB = itemB.offers[selectedOffer].totalCost;
            return compare(totalA, totalB);
        }
    },
];

export const EC2PaymentTypeTable = (items: PaymentType[], currency: string, selectedOffer: SelectProps.Option | null): JSX.Element => {
    return selectedOffer && selectedOffer.value && selectedOffer.label ? (
        <AWSCATTable
            resourceName={selectedOffer.label}
            items={items}
            columnDefinitions={ec2PaymentTypeColumns(currency, selectedOffer.value)}
            defaultSortBy={defaultSortBy('instanceType', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    ) : (<></>);
};

export type VMWareCostClusterTableItem = VMWareCluster & { recommended: string; unusedCost: number; totalCost: number; };

/**
 * Returns a Display Friendly DeterminingFactor If defined
 * @param {string} determiningFactor
 * @returns {string} Display Friendly determiningFactor if available
 */
export function getDisplayFriendlyDeterminingFactor(determiningFactor: string): string {
    const displayName = new Map([
        ['cpu', constants.CPU],
        ['ram', constants.RAM],
        ['storage', constants.STORAGE_CAPITALIZED],
        ['minHosts', constants.MINIMUM_HOSTS_REQUIRED],
        ['maxHosts', constants.MAXIMUM_HOSTS_REACHED],
    ]);

    return displayName.get(determiningFactor) ?? determiningFactor;
}

export const getVMWareClusterCostItems = (items: VMWareCluster[], vmwareCostClusters: VMWareCostCluster[]): VMWareCostClusterTableItem[] => {
    const costClusterMap: Map<string, VMWareCostCluster> = new Map();
    vmwareCostClusters.forEach(cluster => {
        costClusterMap.set(cluster.clusterName, cluster);
    });

    return items.map((cluster: VMWareCluster): VMWareCostClusterTableItem => {
        const clusterCost = costClusterMap.get(cluster.name);
        const enhancedCluster: VMWareCostClusterTableItem = {
            ...cluster,
            recommended: clusterCost ?
                clusterCost.cost.recommended === 'onDemand' ? constants.ON_DEMAND : `${clusterCost?.cost.recommended[0]} ${constants.YEAR_SUBSCRIPTION}`
                : '',
            unusedCost: clusterCost ? clusterCost.cost[clusterCost.cost.recommended].unused : 0 ,
            totalCost: clusterCost ? clusterCost.cost[clusterCost.cost.recommended].total : 0,
        };
        return enhancedCluster;
    });
};

const vmWareClusterCostColumns = (currency: string): ColumnDefinition<VMWareCostClusterTableItem>[] => [
    {
        id: 'name',
        header: constants.CLUSTER_NAME,
        cell: item => item.name,
        type: ColumnTypes.TEXT,
        text: constants.CLUSTER_NAME,
    },
    {
        id: 'hostInstanceType',
        header: constants.HOST_TYPE,
        cell: item => item.hostInstanceType,
        type: ColumnTypes.TEXT,
        text: constants.HOST_TYPE,
    },
    {
        id: 'recommended',
        header: constants.RECOMMENDED_PAYMENT_OPTION,
        cell: item => item.recommended,
        type: ColumnTypes.TEXT,
        text: constants.RECOMMENDED_PAYMENT_OPTION,
    },
    {
        id: 'hostCountDeterminingFactor',
        header: constants.DETERMINING_FACTOR,
        cell: item => getDisplayFriendlyDeterminingFactor(item.hostCountDeterminingFactor),
        type: ColumnTypes.TEXT,
        text: constants.DETERMINING_FACTOR,
    },
    {
        id: 'hostCount',
        header: constants.HOST_COUNT,
        cell: item => item.hostCount,
        type: ColumnTypes.TEXT,
        text: constants.HOST_COUNT,
    },
    {
        id: 'unusedCost',
        header: constants.UNUSED_CAPACITY,
        data: item => roundToInteger(item.unusedCost),
        cell: item => getCell(formatCurrencyNoDecimals(item.unusedCost, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.UNUSED_CAPACITY,
    },
    {
        id: 'totalCost',
        header: constants.TCO_TOTAL_COST,
        data: item => roundToInteger(item.totalCost),
        cell: item => getCell(formatCurrencyNoDecimals(item.totalCost, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.TCO_TOTAL_COST,
    },
];

export const VMWareClusterCostTable = (items: VMWareCostClusterTableItem[], currency: string): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.VMWARE_CLOUD_CLUSTERS}
            items={items}
            columnDefinitions={vmWareClusterCostColumns(currency)}
            defaultSortBy={defaultSortBy('name', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};

const vmWareClusterCashFlowColumns = (currency: string, duration: number): ColumnDefinition<VMWareCostCluster>[] => {
    const columns: ColumnDefinition<VMWareCostCluster>[] = [
        {
            id: 'clusterName',
            header: constants.CLUSTER_NAME,
            cell: item => item.clusterName,
            type: ColumnTypes.TEXT,
            text: constants.CLUSTER_NAME,
        },
        {
            id: 'recommendedPayment',
            header: constants.RECOMMENDED_PAYMENT_OPTION,
            data: item => item.cost.recommended,
            cell: item => item.cost.recommended,
            type: ColumnTypes.TEXT,
            text: constants.RECOMMENDED_PAYMENT_OPTION,
        },
    ];

    if (duration > 0) {
        new Array(duration).fill(0).forEach((_, year) => {
            columns.push({
                id: year.toString(),
                header: `${constants.YEAR} ${year + 1}`,
                data: item => roundToInteger(item.cashFlow[item.cost.recommended].yearly[year]),
                cell: item => getCell(formatCurrencyNoDecimals(item.cashFlow[item.cost.recommended].yearly[year], currency)),
                type: ColumnTypes.NUMBER,
            });
        });
    }

    columns.push({
        id: 'unused',
        header: constants.UNUSED_CAPACITY,
        data: item => item.cost[item.cost.recommended].unused,
        cell: item => getCell(formatCurrencyNoDecimals(item.cost[item.cost.recommended].unused, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.UNUSED_CAPACITY,
    },
    {
        id: 'residual',
        header: constants.RESIDUAL,
        data: item => item.cost[item.cost.recommended].residual,
        cell: item => getCell(formatCurrencyNoDecimals(item.cost[item.cost.recommended].residual, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.RESIDUAL,
    });

    return columns;
};

export const VMWareClusterCashFlowTable = (items: VMWareCostCluster[], currency: string, duration: number): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.VMWARE_CLOUD_CASH_FLOWS}
            items={items}
            columnDefinitions={vmWareClusterCashFlowColumns(currency, duration)}
            defaultSortBy={defaultSortBy('clusterName', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};

const dedicatedHostDetailColumns = (currency: string, pricing: Pricing): ColumnDefinition<DedicatedHostRecommendation>[] => [
    {
        id: 'hostName',
        header: constants.HOST_NAME,
        cell: item => item.hostName,
        type: ColumnTypes.TEXT,
        text: constants.HOST_NAME,
    },
    {
        id: 'dedicatedHostInstanceFamily',
        header: constants.INSTANCE_FAMILY,
        cell: item => item.dedicatedHostInstanceFamily,
        type: ColumnTypes.TEXT,
        text: constants.INSTANCE_FAMILY,
    },
    {
        id: 'vcpuOnUI',
        header: constants.V_CPUS,
        cell: item => item.vcpuOnUI,
        type: ColumnTypes.NUMBER,
        text: constants.V_CPUS,
    },
    {
        id: 'unallocatedVirtualCores',
        header: constants.UNUSED_VCPUS,
        cell: item => item.unallocatedVirtualCores,
        type: ColumnTypes.NUMBER,
        text: constants.UNUSED_VCPUS,
    },
    {
        id: 'recommendedPayment',
        header: constants.RECOMMENDED_PAYMENT_OPTION,
        data: item => pricing.offerDetails[item.lowestOfferName] ?? item.lowestOfferName,
        cell: item => pricing.offerDetails[item.lowestOfferName] ?? item.lowestOfferName,
        type: ColumnTypes.TEXT,
        text: constants.RECOMMENDED_PAYMENT_OPTION,
    },
    {
        id: 'hostCost',
        header: constants.HOST_COST,
        data: item => 
            roundToInteger((item.offers[item.lowestOfferName].total ?? 0) - (item.offers[item.lowestOfferName].cashflow.residual ?? 0)),
        cell: item => getCell(formatCurrencyNoDecimals(
            item.offers[item.lowestOfferName].total ?? 0 - item.offers[item.lowestOfferName].cashflow.residual ?? 0, 
            currency)),
        type: ColumnTypes.NUMBER,
        text: constants.HOST_COST,
    },
    {
        id: 'unusedValue',
        header: constants.UNUSED_VALUE,
        data: item => roundToInteger(item.offers[item.lowestOfferName].unused),
        cell: item => getCell(formatCurrencyNoDecimals(item.offers[item.lowestOfferName].unused ?? 0, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.UNUSED_VALUE,
    },
    {
        id: 'residual',
        header: constants.RESIDUAL,
        data: item => roundToInteger(item.offers[item.lowestOfferName].cashflow.residual),
        cell: item => getCell(formatCurrencyNoDecimals(item.offers[item.lowestOfferName].cashflow.residual ?? 0, currency)),            
        type: ColumnTypes.NUMBER,
        text: constants.RESIDUAL,
    },
    {
        id: 'totalCost',
        header: constants.TCO_TOTAL_COST,
        data: item => roundToInteger((item.offers[item.lowestOfferName].total ?? 0) + (item.offers[item.lowestOfferName].unused ?? 0)),
        cell: item => getCell(formatCurrencyNoDecimals(
            (item.offers[item.lowestOfferName].total ?? 0) + (item.offers[item.lowestOfferName].unused ?? 0), 
            currency)),
        type: ColumnTypes.NUMBER,
        text: constants.TCO_TOTAL_COST,
    },
];

export const DedicatedHostDetailTable = (items: DedicatedHostRecommendation[], currency: string, pricing: Pricing): JSX.Element => {
    return (
        <AWSCATTable
            resourceName={constants.DEDICATED_HOST_DETAILS}
            items={items}
            columnDefinitions={dedicatedHostDetailColumns(currency, pricing)}
            defaultSortBy={defaultSortBy('hostName', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};

type DedicatedHostOSTableItem = {
    resources: string;
    type: string;
    unitCount?: number;
    yearlyCost?: number;
    totalCost: number;
};

const dedicatedHostOSCostColumns = (currency: string): ColumnDefinition<DedicatedHostOSTableItem>[] => [
    {
        id: 'resources',
        header: constants.RESOURCES,
        cell: item => item.resources,
        type: ColumnTypes.TEXT,
        text: constants.RESOURCES,
    },
    {
        id: 'type',
        header: constants.TYPE,
        cell: item => item.type,
        type: ColumnTypes.TEXT,
        text: constants.TYPE,
    },
    {
        id: 'unitCount',
        header: constants.UNIT,
        data: item => item.unitCount || '',
        cell: item => item.unitCount ? getCell(formatNumberAsString(item.unitCount)) : '-',
        type: ColumnTypes.NUMBER,
        text: constants.UNIT,
    },
    {
        id: 'yearlyCost',
        header: constants.ANNUAL_COST,
        data: item => item.yearlyCost ? roundToInteger(item.yearlyCost) : '',
        cell: item => item.yearlyCost ? getCell(formatCurrencyNoDecimals(item.yearlyCost, currency)) : '-',
        type: ColumnTypes.NUMBER,
        text: constants.ANNUAL_COST,
    },
    {
        id: 'totalCost',
        header: constants.TCO_TOTAL_COST,
        data: item => roundToInteger(item.totalCost),
        cell: item => getCell(formatCurrencyNoDecimals(item.totalCost, currency)),
        type: ColumnTypes.NUMBER,
        text: constants.TCO_TOTAL_COST,
    },
];

export const SoftwareAndLicensesCostTable = (
    currency: string, dedicatedHostOsCostResults: DedicatedHostOSCoreResults | undefined,
    sharedTenancyOsCostResults: SharedTenancyOSCostResults | undefined
): JSX.Element => {
    const tableContent: DedicatedHostOSTableItem[] = [];
    if (dedicatedHostOsCostResults) {
        if (dedicatedHostOsCostResults.windowsCost.windowsLicenseCounts > 0) {
            tableContent.push({
                resources: constants.DH_OS_COST_WINDOWS_FOR_DH,
                type: constants.OS_COST_TYPE_THIRD_PARTY,
                unitCount: dedicatedHostOsCostResults.windowsCost.windowsLicenseCounts,
                totalCost: dedicatedHostOsCostResults.windowsCost.windowsLicenseCost,
            }, {
                resources: constants.DH_OS_COST_WINDOWS_ASSURANCE,
                type: constants.OS_COST_TYPE_THIRD_PARTY,
                yearlyCost: dedicatedHostOsCostResults.windowsCost.windowsSupportYearlyCost,
                totalCost: dedicatedHostOsCostResults.windowsCost.windowsSupportTotalCost,
            });
        }
        if (dedicatedHostOsCostResults.sqlServerCost.enterpriseSQLServerLicenseCounts > 0) {
            tableContent.push({
                resources: constants.DH_OS_COST_ENTERPRISE_SQL_FOR_DH,
                type: constants.OS_COST_TYPE_THIRD_PARTY,
                unitCount: dedicatedHostOsCostResults.sqlServerCost.enterpriseSQLServerLicenseCounts,
                totalCost: dedicatedHostOsCostResults.sqlServerCost.enterpriseSQLServerLicenseCost,
            }, {
                resources: constants.DH_OS_COST_ENTERPRISE_SQL_SUPPORT,
                type: constants.OS_COST_TYPE_THIRD_PARTY,
                yearlyCost: dedicatedHostOsCostResults.sqlServerCost.enterpriseSQLServerSupportYearlyCost,
                totalCost: dedicatedHostOsCostResults.sqlServerCost.enterpriseSQLServerSupportTotalCost,
            });
        }
        if (dedicatedHostOsCostResults.sqlServerCost.standardSQLServerLicenseCounts > 0) {
            tableContent.push({
                resources: constants.DH_OS_COST_STANDARD_SQL_FOR_DH,
                type: constants.OS_COST_TYPE_THIRD_PARTY,
                unitCount: dedicatedHostOsCostResults.sqlServerCost.standardSQLServerLicenseCounts,
                totalCost: dedicatedHostOsCostResults.sqlServerCost.standardSQLServerLicenseCost,
            }, {
                resources: constants.DH_OS_COST_STANDARD_SQL_SUPPORT,
                type: constants.OS_COST_TYPE_THIRD_PARTY,
                yearlyCost: dedicatedHostOsCostResults.sqlServerCost.standardSQLServerSupportYearlyCost,
                totalCost: dedicatedHostOsCostResults.sqlServerCost.standardSQLServerSupportTotalCost,
            });
        }
    }
    if (sharedTenancyOsCostResults) {
        if (sharedTenancyOsCostResults.oracleCost.enterpriseOracleLicenseCount > 0) {
            tableContent.push({
                resources: constants.OS_COST_ENTERPRISE_ORACLE,
                type: constants.OS_COST_TYPE_THIRD_PARTY,
                unitCount: sharedTenancyOsCostResults.oracleCost.enterpriseOracleLicenseCount,
                totalCost: sharedTenancyOsCostResults.oracleCost.enterpriseOracleLicenseCost,
            }, {
                resources: constants.OS_COST_ENTERPRISE_ORACLE_SUPPORT,
                type: constants.OS_COST_TYPE_THIRD_PARTY,
                yearlyCost: sharedTenancyOsCostResults.oracleCost.enterpriseOracleSupportYearlyCost,
                totalCost: sharedTenancyOsCostResults.oracleCost.enterpriseOracleSupportTotalCost,
            });
        }
        if (sharedTenancyOsCostResults?.oracleCost.standardOracleLicenseCount > 0) {
            tableContent.push({
                resources: constants.OS_COST_STANDARD_ORACLE,
                type: constants.OS_COST_TYPE_THIRD_PARTY,
                unitCount: sharedTenancyOsCostResults.oracleCost.standardOracleLicenseCount,
                totalCost: sharedTenancyOsCostResults.oracleCost.standardOracleLicenseCost,
            }, {
                resources: constants.OS_COST_STANDARD_ORACLE_SUPPORT,
                type: constants.OS_COST_TYPE_THIRD_PARTY,
                yearlyCost: sharedTenancyOsCostResults.oracleCost.standardOracleSupportYearlyCost,
                totalCost: sharedTenancyOsCostResults.oracleCost.standardOracleSupportTotalCost,
            });
        }
    }

    if (tableContent.length == 0) {
        return (
            <Alert type='info' header={constants.NO_RESULTS_TO_DISPLAY}>
                <p>{constants.NO_HOST_OS_COST_P1_1}<em>{constants.ON}</em> {constants.THE} <em>{constants.NO_HOST_OS_COST_P1_2}</em> {constants.TOGGLE_IN} <em>
                    {constants.NO_HOST_OS_COST_P1_3}</em></p>
                <p>{constants.NO_HOST_OS_COST_P2_1}<em>{constants.ON}</em> {constants.THE} <em>{constants.NO_HOST_OS_COST_P2_2}</em> {constants.TOGGLE_IN} <em>
                    {constants.NO_HOST_OS_COST_P2_3}</em></p>
                <p>{constants.NO_HOST_OS_COST_P3} <a href={getUserGuidePage('dedicatedHostLicenses')} target='_blank'>{constants.USER_GUIDE}</a></p>
            </Alert>
        );
    }
    return (
        <AWSCATTable
            resourceName={constants.SOFTWARE_AND_LICENSES}
            items={tableContent}
            columnDefinitions={dedicatedHostOSCostColumns(currency)}
            defaultSortBy={defaultSortBy('resources', false)}
            autoGenerateVisibleContent
            enableAutoSorting
            useDefaultDownload
        />
    );
};
